/**
 * Space schema validation with Zod
 * @module schemas/space
 */

import { z } from 'zod';
import { SPACE_ROLES } from '../constants/roles.js';
import { userSummarySchema } from './user.schema.js';
import { paginationSchema } from './common.schema.js';

/** Space role schema */
export const spaceRoleSchema = z.enum(SPACE_ROLES);

/** Space key pattern: starts with uppercase letter, followed by alphanumeric/underscore/dash */
const spaceKeyPattern = /^[A-Z][A-Z0-9_-]*$/;

/** Create space request schema */
export const createSpaceRequestSchema = z.object({
  key: z
    .string()
    .min(2, 'Key must be at least 2 characters')
    .max(50, 'Key must not exceed 50 characters')
    .regex(spaceKeyPattern, 'Key must start with uppercase letter and contain only A-Z, 0-9, _, -'),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must not exceed 255 characters'),
  description: z
    .string()
    .max(2000, 'Description must not exceed 2000 characters')
    .optional(),
  iconEmoji: z
    .string()
    .max(10, 'Icon emoji must not exceed 10 characters')
    .optional(),
  isPublic: z.boolean().default(false),
});

/** Update space request schema */
export const updateSpaceRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must not exceed 255 characters')
    .optional(),
  description: z
    .string()
    .max(2000, 'Description must not exceed 2000 characters')
    .optional(),
  iconEmoji: z
    .string()
    .max(10, 'Icon emoji must not exceed 10 characters')
    .optional(),
  isPublic: z.boolean().optional(),
});

/** Space response schema */
export const spaceResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  iconEmoji: z.string().nullable(),
  isPublic: z.boolean(),
  createdBy: userSummarySchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  memberCount: z.number().int().nonnegative(),
  objectCount: z.number().int().nonnegative(),
});

/** Space list response schema */
export const spaceListResponseSchema = z.object({
  data: z.array(spaceResponseSchema),
  pagination: paginationSchema,
});

/** Add space member request schema */
export const addSpaceMemberRequestSchema = z.object({
  userId: z.string().uuid('Invalid user ID'),
  role: spaceRoleSchema,
});

/** Remove space member request schema */
export const removeSpaceMemberRequestSchema = z.object({
  userId: z.string().uuid('Invalid user ID'),
});

/** Space member response schema */
export const spaceMemberResponseSchema = z.object({
  id: z.string().uuid(),
  user: userSummarySchema,
  role: spaceRoleSchema,
  createdAt: z.string().datetime(),
});

/** Space member list response schema */
export const spaceMemberListResponseSchema = z.object({
  data: z.array(spaceMemberResponseSchema),
  pagination: paginationSchema,
});

// Type inference
export type SpaceRoleInput = z.infer<typeof spaceRoleSchema>;
export type CreateSpaceRequestInput = z.infer<typeof createSpaceRequestSchema>;
export type UpdateSpaceRequestInput = z.infer<typeof updateSpaceRequestSchema>;
export type SpaceResponseInput = z.infer<typeof spaceResponseSchema>;
export type SpaceListResponseInput = z.infer<typeof spaceListResponseSchema>;
export type AddSpaceMemberRequestInput = z.infer<typeof addSpaceMemberRequestSchema>;
export type RemoveSpaceMemberRequestInput = z.infer<typeof removeSpaceMemberRequestSchema>;
export type SpaceMemberResponseInput = z.infer<typeof spaceMemberResponseSchema>;
export type SpaceMemberListResponseInput = z.infer<typeof spaceMemberListResponseSchema>;
